import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, Transition } from '@headlessui/react';
import {
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  LanguageIcon,
  Bars3Icon,
  SunIcon,
  MoonIcon,
  ComputerDesktopIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import type { ThemePreference } from '../../contexts/ThemeContext';
import { useDataLanguage } from '../../contexts/DataLanguageContext';
import { NotificationBell } from '../notifications/NotificationBell';

interface HeaderProps {
  onMenuClick: () => void;
  menuOpen?: boolean;
}

const LANGUAGES = [
  { code: 'ar', name: 'العربية', flag: '🇸🇦', dir: 'rtl' },
  { code: 'en', name: 'English', flag: '🇬🇧', dir: 'ltr' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷', dir: 'ltr' },
] as const;

const menuItemsClass =
  'absolute z-50 mt-2 w-44 origin-top-right rounded-xl bg-white shadow-pop ring-1 ring-line focus:outline-none ltr:right-0 rtl:left-0 rtl:origin-top-left overflow-hidden';

const triggerClass =
  'flex items-center gap-2 px-2.5 py-2 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40';

export function Header({ onMenuClick, menuOpen = false }: HeaderProps) {
  const { t, i18n } = useTranslation();
  const { userName, user, logout } = useAuth();
  const { theme, resolvedTheme, setTheme } = useTheme();
  const { showOriginal, setShowOriginal } = useDataLanguage();
  const languageLocked = !!user?.ui_preferences?.language;

  const changeLanguage = (langCode: string) => {
    const lang = LANGUAGES.find(l => l.code === langCode);
    if (lang) {
      i18n.changeLanguage(langCode);
      document.documentElement.lang = langCode;
      document.documentElement.dir = lang.dir;
    }
  };

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  const themeOptions: { value: ThemePreference; label: string; icon: typeof SunIcon }[] = [
    { value: 'light', label: t('theme.light', 'Light'), icon: SunIcon },
    { value: 'dark', label: t('theme.dark', 'Dark'), icon: MoonIcon },
    { value: 'system', label: t('theme.system', 'System'), icon: ComputerDesktopIcon },
  ];

  const dataLanguageOptions = [
    {
      value: false,
      label: t('dataLanguage.translated', 'Translated'),
      description: t('dataLanguage.translatedHint', 'Show product names in the UI language'),
    },
    {
      value: true,
      label: t('dataLanguage.asEntered', 'As entered'),
      description: t('dataLanguage.asEnteredHint', 'Show original product names as typed'),
    },
  ] as const;

  return (
    <header className="bg-surface border-b border-line z-10 flex-shrink-0">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex h-14 items-center justify-between">
          {/* Left: Mobile menu button + Logo */}
          <div className="flex items-center gap-3">
            <button
              onClick={onMenuClick}
              className="lg:hidden p-2.5 -ms-1 rounded-lg text-gray-600 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
              aria-label={t('nav.toggleMenu', 'Toggle navigation menu')}
              aria-expanded={menuOpen}
            >
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>
            <img
              src={i18n.language === 'ar' ? '/images/Logo-ar.png' : '/images/Logo-en.png'}
              alt={t('app.title', 'Loyal International')}
              className="h-9 w-auto object-contain"
              onError={e => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>

          {/* Right: Notifications + Theme + Language + User */}
          <div className="flex items-center gap-1">
            <NotificationBell />

            {/* Theme switcher */}
            <Menu as="div" className="relative">
              <Menu.Button className={triggerClass} aria-label={t('theme.switch', 'Switch theme')}>
                {resolvedTheme === 'dark' ? (
                  <MoonIcon className="h-5 w-5" aria-hidden="true" />
                ) : (
                  <SunIcon className="h-5 w-5" aria-hidden="true" />
                )}
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className={menuItemsClass}>
                  <div className="py-1">
                    {themeOptions.map(option => (
                      <Menu.Item key={option.value}>
                        {({ active }) => (
                          <button
                            onClick={() => setTheme(option.value)}
                            className={`${active ? 'bg-gray-100' : ''} ${
                              theme === option.value
                                ? 'text-brand-700 font-medium'
                                : 'text-gray-700'
                            } group flex w-full items-center gap-3 px-4 py-2 text-sm focus:outline-none focus:bg-gray-100`}
                            role="menuitem"
                          >
                            <option.icon className="h-4 w-4" aria-hidden="true" />
                            <span>{option.label}</span>
                            {theme === option.value && (
                              <span className="ms-auto text-brand-600">✓</span>
                            )}
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>

            {/* Language Selector — hidden when the user has a locked locale */}
            {!languageLocked && (
              <Menu as="div" className="relative">
                <Menu.Button
                  className={triggerClass}
                  aria-label={t('nav.selectLanguage', 'Select language')}
                >
                  <LanguageIcon className="h-5 w-5" aria-hidden="true" />
                  <span className="text-sm font-medium hidden sm:block">{currentLang.name}</span>
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className={menuItemsClass}>
                    <div className="py-1">
                      {LANGUAGES.map(lang => (
                        <Menu.Item key={lang.code}>
                          {({ active }) => (
                            <button
                              onClick={() => changeLanguage(lang.code)}
                              className={`${active ? 'bg-gray-100' : ''} ${
                                i18n.language === lang.code
                                  ? 'text-brand-700 font-medium'
                                  : 'text-gray-700'
                              } group flex w-full items-center gap-3 px-4 py-2 text-sm focus:outline-none focus:bg-gray-100`}
                              role="menuitem"
                            >
                              <span className="text-base">{lang.flag}</span>
                              <span>{lang.name}</span>
                              {i18n.language === lang.code && (
                                <span className="ms-auto text-brand-600">✓</span>
                              )}
                            </button>
                          )}
                        </Menu.Item>
                      ))}
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            )}

            {/* Data language: translated vs as-entered */}
            <Menu as="div" className="relative">
              <Menu.Button
                className={triggerClass}
                aria-label={t('dataLanguage.toggle', 'Data language')}
                title={t('dataLanguage.toggle', 'Data language')}
              >
                <DocumentTextIcon className="h-5 w-5" aria-hidden="true" />
                <span className="text-sm font-medium hidden lg:block">
                  {showOriginal
                    ? t('dataLanguage.asEntered', 'As entered')
                    : t('dataLanguage.translated', 'Translated')}
                </span>
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className={`${menuItemsClass} w-64`}>
                  <div className="py-1">
                    <div className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                      {t('dataLanguage.toggle', 'Data language')}
                    </div>
                    {dataLanguageOptions.map(option => (
                      <Menu.Item key={String(option.value)}>
                        {({ active }) => (
                          <button
                            onClick={() => setShowOriginal(option.value)}
                            className={`${active ? 'bg-gray-100' : ''} ${
                              showOriginal === option.value
                                ? 'text-brand-700 font-medium'
                                : 'text-gray-700'
                            } group flex w-full flex-col items-start gap-0.5 px-4 py-2 text-sm focus:outline-none focus:bg-gray-100`}
                            role="menuitem"
                          >
                            <span className="flex w-full items-center gap-2">
                              <span>{option.label}</span>
                              {showOriginal === option.value && (
                                <span className="ms-auto text-brand-600">✓</span>
                              )}
                            </span>
                            <span className="text-xs font-normal text-gray-500">
                              {option.description}
                            </span>
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>

            {/* User Menu */}
            <Menu as="div" className="relative">
              <Menu.Button
                className={triggerClass}
                aria-label={t('nav.userMenu', 'User menu')}
                aria-haspopup="true"
              >
                <UserCircleIcon className="h-6 w-6" aria-hidden="true" />
                <span className="text-sm font-medium hidden sm:block">{userName || 'User'}</span>
                <span className="sr-only">{userName || 'User'}</span>
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className={`${menuItemsClass} w-48`}>
                  <div className="py-1">
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={logout}
                          className={`${
                            active ? 'bg-gray-100' : ''
                          } group flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 focus:outline-none focus:bg-gray-100`}
                          role="menuitem"
                          aria-label={t('nav.logout', 'Logout')}
                        >
                          <ArrowRightOnRectangleIcon className="h-5 w-5" aria-hidden="true" />
                          {t('nav.logout')}
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </div>
      </div>
    </header>
  );
}
